// src/pages/admin/AffiliateManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { useUI } from '../../context/UIContext';
import { apiClient } from '../../api/apiClient';
import '../../styles/AffiliateManagement.css';

interface Affiliate {
  _id: string;
  affiliateId?: string;
  personalInfo: {
    fullName: string;
    email: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    };
  };
  socialMedia?: {
    platform: string;
    handle: string;
    followerCount: number;
    profileUrl?: string;
  };
  status: string;
  commissionRate: number;
  statistics?: {
    totalClicks: number;
    totalConversions: number;
    totalEarnings: number;
    pendingEarnings?: number;
  };
  referralCode?: string;
  referralLink?: string;
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  suspensionReason?: string;
  rejectionReason?: string;
}

interface TierSetting {
  minConversions: number;
  commissionRate: number;
}

interface AdminCommission {
  _id: string;
  createdAt: string;
  commissionAmount: number;
  bookingTotal: number;
  status: string;
  serviceInfo?: {
    serviceName?: string;
  };
  affiliateId?: {
    personalInfo?: {
      fullName?: string;
      email?: string;
    };
  };
  userId?: {
    name?: string;
    email?: string;
  };
  metadata?: {
    rejectionReason?: string;
  };
}

const TIER_COLORS: Record<string, string> = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#D4AF37',
  platinum: '#6366f1'
};

const AffiliateManagementPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: '-createdAt'
  });
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'suspend' | 'update-rate' | 'reactivate' | null>(null);
  const [actionData, setActionData] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [activeSettingsTab, setActiveSettingsTab] = useState<'booking' | 'registration' | 'commissions'>('registration');
  const [bookingSettings, setBookingSettings] = useState({
    defaultCommissionRate: 3,
    minimumPayout: 50,
    tierSettings: {
      bronze: { minConversions: 0, commissionRate: 3 },
      silver: { minConversions: 100, commissionRate: 4 },
      gold: { minConversions: 300, commissionRate: 5 },
      platinum: { minConversions: 500, commissionRate: 6 }
    } as Record<string, TierSetting>
  });
  const [, setRegistrationSettings] = useState({
    commissionRate: 10, // Single commission rate for all stylist registrations
    baseAmount: 100 // Base amount used to calculate commission
  });
  const [, setIsLoadingSettings] = useState(false);
  const [commissions, setCommissions] = useState<AdminCommission[]>([]);
  const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);
  const [commissionPage, setCommissionPage] = useState(1);
  const [commissionTotalPages, setCommissionTotalPages] = useState(1);
  const [commissionStatusFilter, setCommissionStatusFilter] = useState('all');
  const [commissionStats, setCommissionStats] = useState({
    pendingCount: 0,
    approvedCount: 0,
    pendingAmount: 0
  });
  const [selectedCommission, setSelectedCommission] = useState<AdminCommission | null>(null);
  const [showCommissionRejectModal, setShowCommissionRejectModal] = useState(false);
  const [commissionRejectReason, setCommissionRejectReason] = useState('');
  const [isCommissionActionLoading, setIsCommissionActionLoading] = useState(false);

  useEffect(() => {
    fetchAffiliates();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  useEffect(() => {
    if (activeSettingsTab === 'commissions') {
      fetchCommissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSettingsTab, commissionPage, commissionStatusFilter]);

  const fetchAffiliates = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/v1/admin/affiliates', {
        params: {
          page: currentPage,
          limit: 20,
          sort: filters.sortBy,
          ...(filters.status !== 'all' && { status: filters.status }),
        },
      });
      const data = response.data;

      if (data.success) {
        const affiliatesData = Array.isArray(data.data) ? data.data : (data.data?.affiliates || []);
        setAffiliates(affiliatesData);
        const pagination = data.pagination || data.data?.pagination;
        if (pagination) {
          setCurrentPage(pagination.page || currentPage);
          setTotalPages(pagination.pages || 1);
        }
      }
    } catch (error: any) {
      console.error('Fetch affiliates error:', error);
      showSnackbar(error?.message || 'An error occurred loading referral agents', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCommissions = async () => {
    try {
      setIsLoadingCommissions(true);
      const apiStatus =
        commissionStatusFilter === 'rejected' ? 'cancelled' : commissionStatusFilter;

      const [listResponse, pendingResponse, approvedResponse] = await Promise.all([
        apiClient.get('/v1/admin/affiliates/commissions', {
          params: {
            page: commissionPage,
            limit: 20,
            sortBy: '-createdAt',
            ...(apiStatus !== 'all' && { status: apiStatus }),
          },
        }),
        apiClient.get('/v1/admin/affiliates/commissions', {
          params: { status: 'pending', limit: 500 },
        }),
        apiClient.get('/v1/admin/affiliates/commissions', {
          params: { status: 'approved', limit: 1 },
        }),
      ]);

      const listData = listResponse.data;
      const pendingData = pendingResponse.data;
      const approvedData = approvedResponse.data;

      if (listData.success) {
        let commissionList: AdminCommission[] = listData.data?.commissions || [];
        if (commissionStatusFilter === 'rejected') {
          commissionList = commissionList.filter(c => c.metadata?.rejectionReason);
        } else if (commissionStatusFilter === 'cancelled') {
          commissionList = commissionList.filter(c => !c.metadata?.rejectionReason);
        }
        setCommissions(commissionList);
        const pagination = listData.data?.pagination;
        if (pagination) {
          setCommissionTotalPages(pagination.pages || 1);
        }

        const pendingCommissions: AdminCommission[] = pendingData.data?.commissions || [];
        setCommissionStats({
          pendingCount: pendingData.data?.pagination?.total ?? pendingCommissions.length,
          approvedCount: approvedData.data?.pagination?.total ?? 0,
          pendingAmount: pendingCommissions.reduce((sum, c) => sum + (c.commissionAmount || 0), 0)
        });
      }
    } catch (error: any) {
      console.error('Fetch commissions error:', error);
      showSnackbar(error?.message || 'An error occurred loading commissions', 'error');
    } finally {
      setIsLoadingCommissions(false);
    }
  };

  const handleApproveCommission = async (commissionId: string) => {
    try {
      setIsCommissionActionLoading(true);
      const response = await apiClient.post(
        `/v1/admin/affiliates/commissions/${commissionId}/approve`,
      );
      const data = response.data;
      if (data.success) {
        showSnackbar('Commission approved', 'success');
        fetchCommissions();
      } else {
        showSnackbar(data.message || 'Failed to approve commission', 'error');
      }
    } catch (error) {
      showSnackbar('Error approving commission', 'error');
    } finally {
      setIsCommissionActionLoading(false);
    }
  };

  const handleRejectCommission = async () => {
    if (!selectedCommission) return;
    if (!commissionRejectReason || commissionRejectReason.trim().length < 10) {
      showSnackbar('Please provide a reason (minimum 10 characters)', 'error');
      return;
    }

    try {
      setIsCommissionActionLoading(true);
      const response = await apiClient.post(
        `/v1/admin/affiliates/commissions/${selectedCommission._id}/reject`,
        { reason: commissionRejectReason.trim() },
      );
      const data = response.data;
      if (data.success) {
        showSnackbar('Commission rejected', 'success');
        setShowCommissionRejectModal(false);
        setSelectedCommission(null);
        setCommissionRejectReason('');
        fetchCommissions();
      } else {
        showSnackbar(data.message || 'Failed to reject commission', 'error');
      }
    } catch (error) {
      showSnackbar('Error rejecting commission', 'error');
    } finally {
      setIsCommissionActionLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setIsLoadingSettings(true);
      // Fetch booking settings (default commission rate)
      const bookingResponse = await apiClient.get('/v1/admin/affiliate-settings');

      if (bookingResponse.data?.success) {
        const bookingData = bookingResponse.data;
        if (bookingData.success && bookingData.data) {
          setBookingSettings((prev) => ({
            defaultCommissionRate: bookingData.data.defaultCommissionRate || 3,
            minimumPayout: bookingData.data.minimumPayout || 50,
            tierSettings: bookingData.data.tierSettings
              ? {
                  bronze: {
                    minConversions: bookingData.data.tierSettings.bronze?.minConversions ?? 0,
                    commissionRate: bookingData.data.tierSettings.bronze?.commissionRate ?? 3
                  },
                  silver: {
                    minConversions: bookingData.data.tierSettings.silver?.minConversions ?? 100,
                    commissionRate: bookingData.data.tierSettings.silver?.commissionRate ?? 4
                  },
                  gold: {
                    minConversions: bookingData.data.tierSettings.gold?.minConversions ?? 300,
                    commissionRate: bookingData.data.tierSettings.gold?.commissionRate ?? 5
                  },
                  platinum: {
                    minConversions: bookingData.data.tierSettings.platinum?.minConversions ?? 500,
                    commissionRate: bookingData.data.tierSettings.platinum?.commissionRate ?? 6
                  }
                }
              : prev.tierSettings
          }));
        }
      }

      // Fetch stylist registration settings
      const registrationResponse = await apiClient.get(
        '/v1/admin/affiliates/stylist-commission-rates',
      );

      if (registrationResponse.data?.success) {
        const registrationData = registrationResponse.data;
        if (registrationData.success && registrationData.data) {
          setRegistrationSettings({
            commissionRate: registrationData.data.commissionRate || 10,
            baseAmount: registrationData.data.baseAmount || 100
          });
        }
      }
    } catch (error) {
      console.error('Fetch settings error:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const validateAction = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (actionType === 'reject' || actionType === 'suspend') {
      if (!actionData.reason || actionData.reason.trim().length < 10) {
        errors.reason = 'Please provide a detailed reason (minimum 10 characters)';
      }
    }

    if (actionType === 'update-rate') {
      const rate = parseFloat(actionData.rate);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        errors.rate = 'Commission rate must be between 0 and 100';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAction = async () => {
    if (!selectedAffiliate || !actionType) return;

    // Validate form
    if (!validateAction()) {
      showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    setIsSubmitting(true);
    setValidationErrors({});

    try {
      let endpoint = '';
      let method = 'POST';
      let body: any = {};

      switch (actionType) {
        case 'approve':
          endpoint = `/v1/admin/affiliates/${selectedAffiliate._id}/approve`;
          // Optional: Allow setting commission rate during approval
          if (actionData.commissionRate) {
            body = { commissionRate: parseFloat(actionData.commissionRate) / 100 };
          }
          break;
        case 'reject':
          endpoint = `/v1/admin/affiliates/${selectedAffiliate._id}/reject`;
          body = { reason: actionData.reason.trim() };
          break;
        case 'suspend':
          endpoint = `/v1/admin/affiliates/${selectedAffiliate._id}/suspend`;
          body = { reason: actionData.reason.trim() };
          break;
        case 'reactivate':
          endpoint = `/v1/admin/affiliates/${selectedAffiliate._id}/reactivate`;
          break;
        case 'update-rate':
          endpoint = `/v1/admin/affiliates/${selectedAffiliate._id}/commission-rate`;
          method = 'PUT';
          // Backend accepts decimal (0.10) or percentage (10), we'll send as decimal
          const rate = parseFloat(actionData.rate) / 100;
          body = { commissionRate: rate };
          break;
      }

      const response = method === 'PUT'
        ? await apiClient.put(endpoint, body)
        : await apiClient.post(endpoint, body);
      const data = response.data;

      if (data.success) {
        // Success messages
        const successMessages: { [key: string]: string } = {
          'approve': `Referral Agent "${selectedAffiliate.personalInfo?.fullName}" has been approved successfully`,
          'reject': `Referral Agent "${selectedAffiliate.personalInfo?.fullName}" has been rejected`,
          'suspend': `Referral Agent "${selectedAffiliate.personalInfo?.fullName}" has been suspended`,
          'reactivate': `Referral Agent "${selectedAffiliate.personalInfo?.fullName}" has been reactivated`,
          'update-rate': `Commission rate updated to ${actionData.rate}%`
        };
        
        showSnackbar(successMessages[actionType] || 'Action completed successfully', 'success');
        setShowActionModal(false);
        setSelectedAffiliate(null);
        setActionType(null);
        setActionData({});
        setValidationErrors({});
        // Refresh the list
        await fetchAffiliates();
      }
    } catch (error: any) {
      console.error('Action error:', error);
      if (error?.status === 400 && error?.data?.errors) {
        setValidationErrors(error.data.errors);
      }
      if (error?.status === 404) {
        showSnackbar('Referral agent not found. It may have been deleted.', 'error');
        await fetchAffiliates();
      } else {
        showSnackbar(
          error?.message || 'Network error. Please check your connection and try again.',
          'error',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openActionModal = (affiliate: Affiliate, type: typeof actionType) => {
    setSelectedAffiliate(affiliate);
    setActionType(type);
    setActionData(type === 'update-rate' ? { rate: (affiliate.commissionRate * 100).toFixed(1) } : {});
    setValidationErrors({});
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    if (!isSubmitting) {
      setShowActionModal(false);
      setSelectedAffiliate(null);
      setActionType(null);
      setActionData({});
      setValidationErrors({});
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'suspended':
        return 'badge-error';
      case 'rejected':
        return 'badge-default';
      default:
        return 'badge-default';
    }
  };

  return (
    <div className="affiliate-management">
      <div className="affiliate-management__header">
        <div>
          <h1>Referral Management</h1>
          <p>Manage and monitor all referral agents</p>
        </div>
      </div>

      {/* Settings Tabs */}
      <div className="affiliate-settings-tabs">
        <button
          className={`settings-tab ${activeSettingsTab === 'booking' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('booking')}
        >
          CLIENT BOOKING SETTINGS
        </button>
        <button
          className={`settings-tab ${activeSettingsTab === 'registration' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('registration')}
        >
          STYLIST REGISTRATION SETTINGS
        </button>
        <button
          className={`settings-tab ${activeSettingsTab === 'commissions' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('commissions')}
        >
          COMMISSIONS
        </button>
      </div>

      {/* Settings Content */}
      {activeSettingsTab === 'booking' && (
        <div className="affiliate-settings-section">
          <h2>Client Booking Commission Settings</h2>
          <div className="settings-form">
            <div className="form-group">
              <label>Default Commission Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={bookingSettings.defaultCommissionRate}
                onChange={(e) => setBookingSettings(prev => ({
                  ...prev,
                  defaultCommissionRate: parseFloat(e.target.value) || 0
                }))}
              />
              <small>Default commission rate for client bookings (0-100%)</small>
            </div>
            <div className="form-group">
              <label>Minimum Payout ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={bookingSettings.minimumPayout}
                onChange={(e) => setBookingSettings(prev => ({
                  ...prev,
                  minimumPayout: parseFloat(e.target.value) || 0
                }))}
              />
              <small>Minimum amount required before referral agents can request payout</small>
            </div>

            <div className="affiliate-settings-section" style={{ marginTop: '2rem' }}>
              <style>{`
                @media (max-width: 899px) {
                  .tier-commission-grid {
                    grid-template-columns: repeat(2, 1fr) !important;
                  }
                }
              `}</style>
              <h3 style={{ fontWeight: 600, marginBottom: '4px' }}>Tiered Commission Rates</h3>
              <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: 0 }}>
                Rates increase automatically as referral agents reach conversion milestones.
              </p>
              <div
                className="tier-commission-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                  marginTop: '16px'
                }}
              >
                {(['bronze', 'silver', 'gold', 'platinum'] as const).map((tier) => (
                  <div
                    key={tier}
                    style={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{ height: '5px', backgroundColor: TIER_COLORS[tier] }} />
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 14px',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#ffffff',
                          backgroundColor: TIER_COLORS[tier],
                          marginBottom: '14px',
                          textTransform: 'capitalize'
                        }}
                      >
                        {tier}
                      </span>
                      <div style={{ textAlign: 'left' }}>
                        <label style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                          Min Conversions
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={bookingSettings.tierSettings[tier].minConversions}
                          onChange={(e) => setBookingSettings(prev => ({
                            ...prev,
                            tierSettings: {
                              ...prev.tierSettings,
                              [tier]: {
                                ...prev.tierSettings[tier],
                                minConversions: parseInt(e.target.value, 10) || 0
                              }
                            }
                          }))}
                          style={{ width: '100%', marginBottom: '12px' }}
                        />
                        <label style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                          Commission Rate (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={bookingSettings.tierSettings[tier].commissionRate}
                          onChange={(e) => setBookingSettings(prev => ({
                            ...prev,
                            tierSettings: {
                              ...prev.tierSettings,
                              [tier]: {
                                ...prev.tierSettings[tier],
                                commissionRate: parseFloat(e.target.value) || 0
                              }
                            }
                          }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn-primary" style={{ marginTop: '20px' }} onClick={async () => {
                try {
                  const response = await apiClient.put(
                    '/v1/admin/affiliate-settings',
                    { tierSettings: bookingSettings.tierSettings },
                  );
                  const data = response.data;
                  if (data.success) {
                    showSnackbar('Tier settings updated successfully', 'success');
                  } else {
                    showSnackbar(data.message || 'Failed to update tier settings', 'error');
                  }
                } catch (error) {
                  showSnackbar('Error updating tier settings', 'error');
                }
              }}>
                Save Tier Settings
              </button>
            </div>

            <button className="btn-primary" onClick={async () => {
              try {
                const response = await apiClient.put('/v1/admin/affiliate-settings', bookingSettings);
                const data = response.data;
                if (data.success) {
                  showSnackbar('Booking settings updated successfully', 'success');
                } else {
                  showSnackbar(data.message || 'Failed to update settings', 'error');
                }
              } catch (error) {
                showSnackbar('Error updating settings', 'error');
              }
            }}>
              Save Booking Settings
            </button>
          </div>
        </div>
      )}

      {activeSettingsTab === 'registration' && (
        <div className="affiliate-settings-section">
          <h2>First Appointment Commission Settings</h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem' }}>
            Referral agents earn a one-time commission when a client or stylist they referred completes their first successful appointment (completed and paid). The commission rate is tiered and configured in Client Booking Settings.
          </p>
          <div className="info-box" style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
            <strong>Note:</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
              Commission rates are tiered: starting at 3% and increasing automatically up to 6% as referral agents reach conversion milestones (100, 300, 500). Configure the exact rates in Client Booking Settings.
            </p>
          </div>
        </div>
      )}

      {activeSettingsTab === 'commissions' && (
        <div className="affiliate-settings-section">
          <h2>Commission Management</h2>

          <div className="affiliate-management__filters" style={{ marginBottom: '1.5rem' }}>
            <div className="filter-group">
              <strong>Total Pending:</strong> {commissionStats.pendingCount}
            </div>
            <div className="filter-group">
              <strong>Total Approved:</strong> {commissionStats.approvedCount}
            </div>
            <div className="filter-group">
              <strong>Amount Pending:</strong> {formatCurrency(commissionStats.pendingAmount)}
            </div>
            <div className="filter-group">
              <label htmlFor="commission-status-filter">Status:</label>
              <select
                id="commission-status-filter"
                value={commissionStatusFilter}
                onChange={(e) => {
                  setCommissionStatusFilter(e.target.value);
                  setCommissionPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {isLoadingCommissions ? (
            <div className="affiliate-management__loading">
              <div className="spinner"></div>
              <p>Loading commissions...</p>
            </div>
          ) : commissions.length === 0 ? (
            <div className="affiliate-management__empty">
              <i className="fas fa-receipt"></i>
              <p>No commissions found</p>
            </div>
          ) : (
            <>
              <div className="affiliate-management__table-container">
                <table className="affiliate-management__table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Referral Agent Name</th>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Amount</th>
                      <th>Commission</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <tr key={commission._id}>
                        <td>{formatDate(commission.createdAt)}</td>
                        <td>{commission.affiliateId?.personalInfo?.fullName || 'N/A'}</td>
                        <td>{commission.userId?.name || commission.userId?.email || 'N/A'}</td>
                        <td>{commission.serviceInfo?.serviceName || 'N/A'}</td>
                        <td className="amount">{formatCurrency(commission.bookingTotal || 0)}</td>
                        <td className="amount">{formatCurrency(commission.commissionAmount || 0)}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(commission.status)}`}>
                            {commission.metadata?.rejectionReason ? 'rejected' : commission.status}
                          </span>
                        </td>
                        <td>
                          {commission.status === 'pending' && (
                            <div className="action-buttons">
                              <button
                                onClick={() => handleApproveCommission(commission._id)}
                                className="btn-action btn-action--success"
                                title="Approve"
                                disabled={isCommissionActionLoading}
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedCommission(commission);
                                  setCommissionRejectReason('');
                                  setShowCommissionRejectModal(true);
                                }}
                                className="btn-action btn-action--error"
                                title="Reject"
                                disabled={isCommissionActionLoading}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {commissionTotalPages > 1 && (
                <div className="affiliate-management__pagination">
                  <button
                    onClick={() => setCommissionPage(prev => prev - 1)}
                    disabled={commissionPage === 1}
                    className="pagination-btn"
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  <div className="pagination-info">
                    Page {commissionPage} of {commissionTotalPages}
                  </div>
                  <button
                    onClick={() => setCommissionPage(prev => prev + 1)}
                    disabled={commissionPage === commissionTotalPages}
                    className="pagination-btn"
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Filters */}
      {activeSettingsTab !== 'commissions' && (
      <div className="affiliate-management__filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sort-filter">Sort By:</label>
          <select
            id="sort-filter"
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
          >
            <option value="-createdAt">Newest First</option>
            <option value="createdAt">Oldest First</option>
            <option value="-statistics.totalEarnings">Highest Earnings</option>
            <option value="-statistics.totalConversions">Most Conversions</option>
          </select>
        </div>
      </div>
      )}

      {/* Referral Agents Table */}
      {activeSettingsTab !== 'commissions' && (
      <>
      {isLoading ? (
        <div className="affiliate-management__loading">
          <div className="spinner"></div>
          <p>Loading referral agents...</p>
        </div>
      ) : affiliates.length === 0 ? (
        <div className="affiliate-management__empty">
          <i className="fas fa-users"></i>
          <p>No referral agents found</p>
        </div>
      ) : (
        <>
          <div className="affiliate-management__table-container">
            <table className="affiliate-management__table">
              <thead>
                <tr>
                  <th>Referral Agent</th>
                  <th>Social Media</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                  <th>Earnings</th>
                  <th>Commission Rate</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((affiliate) => (
                  <tr key={affiliate._id}>
                    <td>
                      <div className="affiliate-info">
                        <strong>{affiliate.personalInfo?.fullName || 'N/A'}</strong>
                        <small>{affiliate.personalInfo?.email || 'N/A'}</small>
                      </div>
                    </td>
                    <td>
                      <div className="social-info">
                        <span className="platform">{affiliate.socialMedia?.platform || 'N/A'}</span>
                        <small>{(affiliate.socialMedia?.followerCount || 0).toLocaleString()} followers</small>
                      </div>
                    </td>
                    <td>{(affiliate.statistics?.totalClicks || 0).toLocaleString()}</td>
                    <td>{(affiliate.statistics?.totalConversions || 0).toLocaleString()}</td>
                    <td className="amount">{formatCurrency(affiliate.statistics?.totalEarnings || 0)}</td>
                    <td>{(affiliate.commissionRate * 100).toFixed(1)}%</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(affiliate.status)}`}>
                        {affiliate.status}
                      </span>
                    </td>
                    <td>{formatDate(affiliate.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {affiliate.status === 'pending' && (
                          <>
                            <button
                              onClick={() => openActionModal(affiliate, 'approve')}
                              className="btn-action btn-action--success"
                              title="Approve"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              onClick={() => openActionModal(affiliate, 'reject')}
                              className="btn-action btn-action--error"
                              title="Reject"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        {affiliate.status === 'active' && (
                          <>
                            <button
                              onClick={() => openActionModal(affiliate, 'update-rate')}
                              className="btn-action btn-action--primary"
                              title="Update Commission Rate"
                            >
                              <i className="fas fa-percent"></i>
                            </button>
                            <button
                              onClick={() => openActionModal(affiliate, 'suspend')}
                              className="btn-action btn-action--warning"
                              title="Suspend Referral Agent"
                            >
                              <i className="fas fa-ban"></i>
                            </button>
                          </>
                        )}
                        {affiliate.status === 'suspended' && (
                          <button
                            onClick={() => openActionModal(affiliate, 'reactivate')}
                            className="btn-action btn-action--success"
                            title="Reactivate Referral Agent"
                          >
                            <i className="fas fa-check-circle"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="affiliate-management__pagination">
              <button
                onClick={() => setCurrentPage(prev => prev - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Commission Reject Modal */}
      {showCommissionRejectModal && selectedCommission && (
        <div className="modal-overlay" onClick={() => !isCommissionActionLoading && setShowCommissionRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Commission</h2>
              <button
                onClick={() => setShowCommissionRejectModal(false)}
                className="modal-close"
                disabled={isCommissionActionLoading}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Reject commission of {formatCurrency(selectedCommission.commissionAmount)} for{' '}
                {selectedCommission.affiliateId?.personalInfo?.fullName || 'this referral agent'}?
              </p>
              <div className="form-group">
                <label htmlFor="commission-reject-reason">Reason *</label>
                <textarea
                  id="commission-reject-reason"
                  value={commissionRejectReason}
                  onChange={(e) => setCommissionRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection (minimum 10 characters)..."
                  rows={4}
                  disabled={isCommissionActionLoading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowCommissionRejectModal(false)}
                className="btn-secondary"
                disabled={isCommissionActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectCommission}
                className="btn-primary"
                disabled={isCommissionActionLoading}
              >
                {isCommissionActionLoading ? 'Rejecting...' : 'Reject Commission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedAffiliate && (
        <div className="modal-overlay" onClick={closeActionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {actionType === 'approve' && 'Approve Referral Agent'}
                {actionType === 'reject' && 'Reject Referral Agent'}
                {actionType === 'suspend' && 'Suspend Referral Agent'}
                {actionType === 'reactivate' && 'Reactivate Referral Agent'}
                {actionType === 'update-rate' && 'Update Commission Rate'}
              </h2>
              <button 
                onClick={closeActionModal} 
                className="modal-close"
                disabled={isSubmitting}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="affiliate-summary">
                <p><strong>Referral Agent:</strong> {selectedAffiliate.personalInfo?.fullName || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedAffiliate.personalInfo?.email || 'N/A'}</p>
                {selectedAffiliate.affiliateId && (
                  <p><strong>ID:</strong> {selectedAffiliate.affiliateId}</p>
                )}
                {selectedAffiliate.status && (
                  <p><strong>Current Status:</strong> <span className={`badge ${getStatusBadgeClass(selectedAffiliate.status)}`}>{selectedAffiliate.status}</span></p>
                )}
              </div>
              
              {actionType === 'approve' && (
                <div className="action-confirmation">
                  <p>Are you sure you want to approve this referral agent?</p>
                  <p className="action-note">The referral agent will be notified via email and will be able to start earning commissions.</p>
                </div>
              )}

              {actionType === 'reactivate' && (
                <div className="action-confirmation">
                  <p>Are you sure you want to reactivate this referral agent?</p>
                  <p className="action-note">The referral agent will be able to resume earning commissions.</p>
                  {selectedAffiliate.suspensionReason && (
                    <div className="info-box" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f0f0', borderRadius: '4px' }}>
                      <strong>Previous suspension reason:</strong>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>{selectedAffiliate.suspensionReason}</p>
                    </div>
                  )}
                </div>
              )}

              {(actionType === 'reject' || actionType === 'suspend') && (
                <div className="form-group">
                  <label htmlFor="reason">
                    Reason * 
                    <span className="char-count" style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                      ({actionData.reason?.length || 0}/10 minimum)
                    </span>
                  </label>
                  <textarea
                    id="reason"
                    value={actionData.reason || ''}
                    onChange={(e) => {
                      setActionData({ reason: e.target.value });
                      if (validationErrors.reason) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.reason;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Enter a detailed reason for this action (minimum 10 characters)..."
                    rows={4}
                    required
                    disabled={isSubmitting}
                    className={validationErrors.reason ? 'error' : ''}
                  />
                  {validationErrors.reason && (
                    <span className="error-message" style={{ color: '#C0392B', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {validationErrors.reason}
                    </span>
                  )}
                  <p className="form-hint" style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                    This reason will be sent to the referral agent via email.
                  </p>
                </div>
              )}

              {actionType === 'update-rate' && (
                <div className="form-group">
                  <label htmlFor="rate">
                    Commission Rate (%) *
                    {selectedAffiliate.commissionRate && (
                      <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                        (Current: {(selectedAffiliate.commissionRate * 100).toFixed(1)}%)
                      </span>
                    )}
                  </label>
                  <input
                    id="rate"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={actionData.rate || ''}
                    onChange={(e) => {
                      setActionData({ rate: e.target.value });
                      if (validationErrors.rate) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.rate;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="10.0"
                    required
                    disabled={isSubmitting}
                    className={validationErrors.rate ? 'error' : ''}
                  />
                  {validationErrors.rate && (
                    <span className="error-message" style={{ color: '#C0392B', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {validationErrors.rate}
                    </span>
                  )}
                  <p className="form-hint" style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                    Enter a percentage between 0 and 100. This rate will apply to all future commissions.
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                onClick={closeActionModal} 
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                onClick={handleAction} 
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" style={{ 
                      display: 'inline-block', 
                      width: '14px', 
                      height: '14px', 
                      border: '2px solid #fff', 
                      borderTopColor: 'transparent', 
                      borderRadius: '50%', 
                      marginRight: '8px',
                      animation: 'spin 0.6s linear infinite'
                    }}></span>
                    Processing...
                  </>
                ) : (
                  <>
                    {actionType === 'approve' && 'Approve'}
                    {actionType === 'reject' && 'Reject'}
                    {actionType === 'suspend' && 'Suspend'}
                    {actionType === 'reactivate' && 'Reactivate'}
                    {actionType === 'update-rate' && 'Update Rate'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateManagementPage;
